import React, { Suspense, lazy, useEffect, useMemo, useState } from "react";
import "./App.css";
import { AboutPage, WooCommerceDocsPage } from "./Pages";
import { SavingsPage, SavingsTeaser } from "./Savings";
import CookieBanner from "./CookieBanner";
import { COMPANY, REGULATORY_LINE } from "./company";
import { WOO_APPLY_URL, WOO_PLAN_PRICING, WOO_PLAN_VAT_NOTE } from "./plans";
import {
  isHubSpotLoaded,
  loadHubSpot,
  readConsent,
  reloadPage,
  removeHubSpotCookies,
  stopHubSpot,
  writeConsent,
} from "./consent";
import {
  LEGAL_PAGES,
  NavContext,
  PAGE_DESCRIPTIONS,
  PAGE_TITLES,
  PAGE_TO_PATH,
  SITE_ORIGIN,
  SiteLink,
  pageFromPath,
} from "./routes";

const LegalPage = lazy(() => import("./legal/LegalPage"));

/* If the on-demand legal chunk fails to load (e.g. after a deploy), offer a full reload */
class LegalPageBoundary extends React.Component {
  state = { failed: false };

  static getDerivedStateFromError() {
    return { failed: true };
  }

  render() {
    if (!this.state.failed) return this.props.children;
    return (
      <p className="legalLoading">
        This page didn't load.{" "}
        <a className="legalLink" href={window.location.pathname + window.location.hash}>Try again</a>
      </p>
    );
  }
}

/* ──────────────────────────────────────────────
   HubSpot configuration
   Portal: 147192179 (EU region)
   Form: c55b496c-a832-46f7-82ef-fd812d5feecf
   ────────────────────────────────────────────── */
const HUBSPOT_PORTAL_ID = "147192179";
const HUBSPOT_FORM_GUID = "c55b496c-a832-46f7-82ef-fd812d5feecf";

const DASHBOARD_URL = "https://dashboard.paybank2bank.io/";
const CONNECT_URL = "https://connect.paybank2bank.io/";
const SUPPORT_EMAIL = COMPANY.email;

const openExternal = (url) => {
  window.open(url, "_blank", "noopener,noreferrer");
};

/* Sets a <meta>/<link> tag in <head>, creating it if needed */
const setHeadTag = (selector, create, attr, value) => {
  let el = document.head.querySelector(selector);
  if (!el) {
    el = create();
    document.head.appendChild(el);
  }
  el.setAttribute(attr, value);
};

/* ──────────────────────────────────────────────
   Inline SVG icon set
   ────────────────────────────────────────────── */
const Icon = {
  Select: () => (
    <svg viewBox="0 0 32 32" fill="none" aria-hidden="true">
      <rect x="4" y="6" width="24" height="20" rx="4" stroke="currentColor" strokeWidth="2" />
      <path d="M9 13h14M9 17h10M9 21h6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
      <circle cx="23" cy="21" r="3" fill="currentColor" />
      <path d="m21.6 21.2 1 1 2-2.2" stroke="white" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  ),
  Authorise: () => (
    <svg viewBox="0 0 32 32" fill="none" aria-hidden="true">
      <rect x="6" y="3" width="20" height="26" rx="4" stroke="currentColor" strokeWidth="2" />
      <path d="M12 11h8M12 15h8M12 19h5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
      <circle cx="16" cy="24" r="2" fill="currentColor" />
    </svg>
  ),
  Confirmed: () => (
    <svg viewBox="0 0 32 32" fill="none" aria-hidden="true">
      <circle cx="16" cy="16" r="12" stroke="currentColor" strokeWidth="2" />
      <path d="m10.5 16.5 4 4 7.5-8" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  ),
  Conversion: () => (
    <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path d="M3 17 9 11l4 4 8-9" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M15 6h6v6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  ),
  Brand: () => (
    <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path d="M12 2 4 6v6c0 5 3.4 9.4 8 10 4.6-.6 8-5 8-10V6l-8-4Z" stroke="currentColor" strokeWidth="2" strokeLinejoin="round" />
      <path d="m9 12 2 2 4-4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  ),
  Ops: () => (
    <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <rect x="3" y="4" width="18" height="16" rx="2" stroke="currentColor" strokeWidth="2" />
      <path d="M3 9h18M8 14l2 2 4-4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  ),
  Scale: () => (
    <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path d="M4 20V8m4 12V4m4 16v-9m4 9V7m4 13V11" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
    </svg>
  ),
  Globe: () => (
    <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="2" />
      <path d="M3 12h18M12 3c3 3.5 3 14 0 18M12 3c-3 3.5-3 14 0 18" stroke="currentColor" strokeWidth="2" />
    </svg>
  ),
  Bolt: () => (
    <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path d="M13 2 4 14h7l-1 8 9-12h-7l1-8Z" stroke="currentColor" strokeWidth="2" strokeLinejoin="round" />
    </svg>
  ),
  Shield: () => (
    <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path d="M12 2 4 6v6c0 5 3.4 9.4 8 10 4.6-.6 8-5 8-10V6l-8-4Z" stroke="currentColor" strokeWidth="2" strokeLinejoin="round" />
    </svg>
  ),
  Sparkle: () => (
    <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path d="M12 3v18M3 12h18M6 6l12 12M18 6 6 18" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
    </svg>
  ),
  Link: () => (
    <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path d="M10 14a4 4 0 0 0 5.66 0l3-3a4 4 0 1 0-5.66-5.66l-1 1" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
      <path d="M14 10a4 4 0 0 0-5.66 0l-3 3a4 4 0 1 0 5.66 5.66l1-1" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
    </svg>
  ),
  Verify: () => (
    <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path d="M12 2 4 6v6c0 5 3.4 9.4 8 10 4.6-.6 8-5 8-10V6l-8-4Z" stroke="currentColor" strokeWidth="2" strokeLinejoin="round" />
      <path d="m9 12 2 2 4-4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  ),
  Card: () => (
    <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <rect x="3" y="5" width="18" height="14" rx="3" stroke="currentColor" strokeWidth="2" />
      <path d="M3 10h18M7 15h4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
    </svg>
  ),
};

/* ──────────────────────────────────────────────
   Bank brand marks
   Pulls official logo files from /public/banks/ —
   drop a SVG/PNG named after the bank into that
   folder and it will be used automatically. A
   brand-coloured monogram is rendered as a
   fallback while the file is missing or loading.
   ────────────────────────────────────────────── */
const BANK_DEFS = {
  Barclays: { file: "barclays.svg", bg: "#00AEEF", letter: "B", rounded: "circle" },
  HSBC:     { file: "hsbc.svg",     bg: "#DB0011", letter: "H", rounded: "rect"   },
  NatWest:  { file: "natwest.svg",  bg: "#42145F", letter: "N", rounded: "circle" },
  Monzo:    { file: "monzo.svg",    bg: "#FF4F40", letter: "M", rounded: "circle" },
  Revolut:  { file: "revolut.svg",  bg: "#0A0E17", letter: "R", rounded: "rect"   },
  Starling: { file: "starling.svg", bg: "#6935D3", letter: "S", rounded: "circle" },
};

function BankBadgeFallback({ bg, letter, rounded }) {
  return (
    <svg viewBox="0 0 32 32" aria-hidden="true">
      {rounded === "circle" ? (
        <circle cx="16" cy="16" r="16" fill={bg} />
      ) : (
        <rect width="32" height="32" rx="7" fill={bg} />
      )}
      <text
        x="16" y="16"
        textAnchor="middle" dominantBaseline="central"
        fontFamily="Inter, -apple-system, BlinkMacSystemFont, sans-serif"
        fontSize="17" fontWeight="800" fill="#FFFFFF"
        letterSpacing="-0.5"
      >
        {letter}
      </text>
    </svg>
  );
}

function BankMark({ name }) {
  const def = BANK_DEFS[name];
  const [failed, setFailed] = useState(false);
  if (failed || !def) return <BankBadgeFallback {...(def || { bg: "#999", letter: "?", rounded: "circle" })} />;
  return (
    <img
      src={`/banks/${def.file}`}
      alt={`${name} logo`}
      className="bankImg"
      onError={() => setFailed(true)}
    />
  );
}

const BankLogo = {
  Barclays: () => <BankMark name="Barclays" />,
  HSBC:     () => <BankMark name="HSBC" />,
  NatWest:  () => <BankMark name="NatWest" />,
  Monzo:    () => <BankMark name="Monzo" />,
  Revolut:  () => <BankMark name="Revolut" />,
  Starling: () => <BankMark name="Starling" />,
};

/* ──────────────────────────────────────────────
   Tiny country flags (CSS pill, no images required)
   ────────────────────────────────────────────── */
const COUNTRIES = [
  { code: "AT", name: "Austria",        flag: "🇦🇹" },
  { code: "BE", name: "Belgium",        flag: "🇧🇪" },
  { code: "CZ", name: "Czechia",        flag: "🇨🇿" },
  { code: "DE", name: "Germany",        flag: "🇩🇪" },
  { code: "DK", name: "Denmark",        flag: "🇩🇰" },
  { code: "EE", name: "Estonia",        flag: "🇪🇪" },
  { code: "ES", name: "Spain",          flag: "🇪🇸" },
  { code: "FI", name: "Finland",        flag: "🇫🇮" },
  { code: "FR", name: "France",         flag: "🇫🇷" },
  { code: "HU", name: "Hungary",        flag: "🇭🇺" },
  { code: "IE", name: "Ireland",        flag: "🇮🇪" },
  { code: "IT", name: "Italy",          flag: "🇮🇹" },
  { code: "LT", name: "Lithuania",      flag: "🇱🇹" },
  { code: "LV", name: "Latvia",         flag: "🇱🇻" },
  { code: "NL", name: "Netherlands",    flag: "🇳🇱" },
  { code: "NO", name: "Norway",         flag: "🇳🇴" },
  { code: "PL", name: "Poland",         flag: "🇵🇱" },
  { code: "PT", name: "Portugal",       flag: "🇵🇹" },
  { code: "RO", name: "Romania",        flag: "🇷🇴" },
  { code: "SE", name: "Sweden",         flag: "🇸🇪" },
  { code: "UK", name: "United Kingdom", flag: "🇬🇧" },
].sort((a, b) => a.name.localeCompare(b.name));

/* ──────────────────────────────────────────────
   Country carousel — auto-scrolling, alphabetical
   ────────────────────────────────────────────── */
function CountryCarousel({ countries }) {
  // Duplicate list once to make the marquee loop seamlessly
  const loop = [...countries, ...countries];
  return (
    <div className="carousel" aria-label="Countries we operate in" role="region">
      <div className="carouselTrack">
        {loop.map((c, i) => (
          <div key={`${c.code}-${i}`} className="carouselCard">
            <span className="carouselFlag" aria-hidden="true">{c.flag}</span>
            <div className="carouselTextWrap">
              <span className="carouselName">{c.name}</span>
              <span className="carouselCode">{c.code}</span>
            </div>
          </div>
        ))}
      </div>
      <div className="carouselFadeLeft" aria-hidden="true" />
      <div className="carouselFadeRight" aria-hidden="true" />
    </div>
  );
}

export default function App() {
  const [page, setPage] = useState(() =>
    typeof window === "undefined" ? "home" : pageFromPath(window.location.pathname)
  );
  const [navOpen, setNavOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [activeSection, setActiveSection] = useState("top");
  const [formSent, setFormSent] = useState(false);
  const [formSending, setFormSending] = useState(false);
  const [formError, setFormError] = useState(false);
  const [contactPrefill, setContactPrefill] = useState("");
  const [consent, setConsent] = useState(() => (typeof window === "undefined" ? null : readConsent()));
  const [bannerOpen, setBannerOpen] = useState(() => consent === null);
  const [scrollTarget, setScrollTarget] = useState(() =>
    typeof window === "undefined" ? null : window.location.hash.slice(1) || null
  );

  const sections = useMemo(
    () => [
      { id: "how-it-works", label: "How it works" },
      { id: "savings", label: "Savings" },
      { id: "products", label: "Products" },
      { id: "coverage", label: "Coverage" },
      { id: "developers", label: "Developers" },
      { id: "contact", label: "Contact" },
    ],
    []
  );

  // Nav scroll effect
  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // Browser back / forward
  useEffect(() => {
    const onPop = () => {
      setPage(pageFromPath(window.location.pathname));
      setScrollTarget(window.location.hash.slice(1) || null);
    };
    window.addEventListener("popstate", onPop);
    return () => window.removeEventListener("popstate", onPop);
  }, []);

  // Document title, description and canonical URL per page
  useEffect(() => {
    document.title = PAGE_TITLES[page] || PAGE_TITLES.home;
    setHeadTag(
      'meta[name="description"]',
      () => Object.assign(document.createElement("meta"), { name: "description" }),
      "content",
      PAGE_DESCRIPTIONS[page] || PAGE_DESCRIPTIONS.home
    );
    setHeadTag(
      'link[rel="canonical"]',
      () => Object.assign(document.createElement("link"), { rel: "canonical" }),
      "href",
      SITE_ORIGIN + (PAGE_TO_PATH[page] || "/")
    );
  }, [page]);

  // Scroll to a section once the page holding it has rendered (page#section links)
  useEffect(() => {
    if (!scrollTarget) return;
    document.getElementById(scrollTarget)?.scrollIntoView({ block: "start" });
    setScrollTarget(null);
  }, [scrollTarget, page]);

  // HubSpot analytics loads only after the visitor opts in
  useEffect(() => {
    if (consent?.analytics) loadHubSpot(HUBSPOT_PORTAL_ID);
  }, [consent]);

  // Without an opt-in, clear HubSpot cookies left from earlier visits
  useEffect(() => {
    if (!readConsent()?.analytics) removeHubSpotCookies();
  }, []);

  // ESC to close mobile nav
  useEffect(() => {
    const onKeyDown = (e) => {
      if (e.key === "Escape") setNavOpen(false);
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, []);

  // Active section tracking
  useEffect(() => {
    if (page !== "home") return;
    const observer = new IntersectionObserver(
      (entries) => {
        const visible = entries
          .filter((e) => e.isIntersecting)
          .sort((a, b) => b.intersectionRatio - a.intersectionRatio)[0];
        if (visible?.target?.id) setActiveSection(visible.target.id);
      },
      { threshold: [0.2, 0.4, 0.6] }
    );
    sections.forEach((s) => {
      const el = document.getElementById(s.id);
      if (el) observer.observe(el);
    });
    return () => observer.disconnect();
  }, [sections, page]);

  // Scroll-reveal animations
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) entry.target.classList.add("revealed");
        });
      },
      { threshold: 0.1, rootMargin: "0px 0px -40px 0px" }
    );
    document.querySelectorAll(".reveal").forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, [page]);

  const pushPath = (path) => {
    if (window.location.pathname + window.location.hash !== path) {
      window.history.pushState({}, "", path);
    }
  };

  const goHome = (anchor = "top") => {
    setNavOpen(false);
    if (page !== "home") {
      setPage("home");
      pushPath("/");
      window.scrollTo({ top: 0, behavior: "auto" });
      if (anchor !== "top") {
        setTimeout(() => {
          document.getElementById(anchor)?.scrollIntoView({ behavior: "smooth", block: "start" });
        }, 80);
      }
      return;
    }
    if (anchor === "top") {
      window.scrollTo({ top: 0, behavior: "smooth" });
      return;
    }
    document.getElementById(anchor)?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const goPage = (name, hash) => {
    setNavOpen(false);
    setPage(name);
    pushPath((PAGE_TO_PATH[name] || "/") + (hash ? `#${hash}` : ""));
    if (hash) setScrollTarget(hash);
    else window.scrollTo({ top: 0, behavior: "auto" });
  };

  // Cookie banner choice; rejecting removes HubSpot's cookies, and its scripts if they are running
  const chooseCookies = (analytics) => {
    setConsent(writeConsent(analytics));
    setBannerOpen(false);
    if (analytics) return;
    if (isHubSpotLoaded()) stopHubSpot();
    removeHubSpotCookies();
    if (isHubSpotLoaded()) reloadPage();
  };

  // Calculator CTA — carries the visitor's estimate into the contact form
  const goContactWithSummary = (summary) => {
    setContactPrefill(summary);
    setFormSent(false);
    goHome("contact");
  };

  // ── HubSpot form submission ──
  const handleFormSubmit = async (e) => {
    e.preventDefault();
    setFormSending(true);
    setFormError(false);

    const fd = new FormData(e.target);
    const firstName = fd.get("firstname") || "";
    const lastName = fd.get("lastname") || "";
    const email = fd.get("email") || "";
    const company = fd.get("company") || "";
    const industryVal = fd.get("industry") || "";
    const useCaseVal = fd.get("usecase") || "";
    const rawMessage = fd.get("message") || "";

    const products = fd.getAll("products");
    const regions = fd.getAll("regions");

    const messageParts = [];
    if (products.length) messageParts.push("Products: " + products.join(", "));
    if (regions.length) messageParts.push("Region interest: " + regions.join(", "));
    if (useCaseVal) messageParts.push("Use case: " + useCaseVal);
    if (rawMessage) messageParts.push("\n" + rawMessage);
    const fullMessage = messageParts.join("\n");

    // Only link the enquiry to browsing history when analytics cookies are allowed
    const analyticsAllowed = Boolean(consent?.analytics);

    try {
      const res = await fetch(
        `https://api.hsforms.com/submissions/v3/integration/submit/${HUBSPOT_PORTAL_ID}/${HUBSPOT_FORM_GUID}`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            fields: [
              { objectTypeId: "0-1", name: "email", value: email },
              { objectTypeId: "0-1", name: "firstname", value: firstName },
              { objectTypeId: "0-1", name: "lastname", value: lastName },
              { objectTypeId: "0-1", name: "company", value: company },
              { objectTypeId: "0-1", name: "industry", value: industryVal },
              { objectTypeId: "0-1", name: "message", value: fullMessage },
            ],
            context: {
              pageUri: window.location.href,
              pageName: "Paybank2bank - Contact",
              hutk: analyticsAllowed ? document.cookie.match(/hubspotutk=([^;]*)/)?.[1] : undefined,
            },
          }),
        }
      );
      if (!res.ok) throw new Error(`HubSpot responded ${res.status}`);
      if (analyticsAllowed) {
        const _hsq = (window._hsq = window._hsq || []);
        _hsq.push(["identify", { email, firstname: firstName, lastname: lastName, company }]);
        _hsq.push(["trackPageView"]);
      }
      setFormSent(true);
    } catch (_) {
      setFormError(true);
    }

    setFormSending(false);
  };

  const banks = [
    { name: "Barclays", Logo: BankLogo.Barclays },
    { name: "HSBC", Logo: BankLogo.HSBC },
    { name: "NatWest", Logo: BankLogo.NatWest },
    { name: "Monzo", Logo: BankLogo.Monzo },
    { name: "Revolut", Logo: BankLogo.Revolut },
    { name: "Starling", Logo: BankLogo.Starling },
  ];

  const content = (
    <div className={`app ${bannerOpen ? "appWithCookieBanner" : ""}`}>
      {/* ═══ NAV ═══ */}
      <header className={`nav ${scrolled ? "navScrolled" : ""} ${navOpen ? "navOpen" : ""}`}>
        <div className="navInner">
          <button className="brand" onClick={() => goHome("top")} aria-label="Go to top">
            <img
              className="brandLogo"
              src="/pb2b-logo.png"
              alt="paybank2bank"
              onError={(e) => { e.currentTarget.style.display = "none"; }}
            />
            <span className="brandWordmark">
              <span className="brandName">paybank2bank</span>
              <span className="brandTagline">Premium Open Banking payments &amp; verification</span>
            </span>
          </button>

          {page === "home" && (
            <nav className="navLinks" aria-label="Primary">
              {sections.map((s) => (
                <button
                  key={s.id}
                  className={`navLink ${activeSection === s.id ? "navLinkActive" : ""}`}
                  onClick={() => goHome(s.id)}
                >
                  {s.label}
                </button>
              ))}
            </nav>
          )}

          <div className="navActions">
            {page !== "home" && (
              <button className="btn btnSecondary" onClick={() => goHome("top")}>
                Back to home
              </button>
            )}
            <button className="btn btnAccent btnGlow" onClick={() => goHome("contact")}>
              Contact us
            </button>
            <button className="btn btnPrimary" onClick={() => openExternal(DASHBOARD_URL)}>
              Dashboard
            </button>
            <button
              className="burger"
              aria-label={navOpen ? "Close menu" : "Open menu"}
              aria-expanded={navOpen}
              onClick={() => setNavOpen((v) => !v)}
            >
              <span className="burgerBar" />
              <span className="burgerBar" />
              <span className="burgerBar" />
            </button>
          </div>
        </div>

        {/* Mobile drawer */}
        <div className="navDrawer" role="dialog" aria-label="Menu">
          <div className="navDrawerInner">
            {page === "home" ? (
              sections.map((s) => (
                <button key={s.id} className="navDrawerLink" onClick={() => goHome(s.id)}>
                  {s.label}
                </button>
              ))
            ) : (
              <button className="navDrawerLink" onClick={() => goHome("top")}>Back to home</button>
            )}
            <div className="navDrawerCtas">
              <button className="btn btnAccent btnGlow" onClick={() => goHome("contact")}>
                Contact us
              </button>
              <button className="btn btnPrimary" onClick={() => openExternal(DASHBOARD_URL)}>
                Dashboard
              </button>
            </div>
          </div>
        </div>
      </header>

      <main>
        {page === "about" && <div className="legalWrap"><div className="container"><AboutPage /></div></div>}
        {LEGAL_PAGES.includes(page) && (
          <div className="legalWrap">
            <div className="container">
              <LegalPageBoundary key={page}>
                <Suspense fallback={<p className="legalLoading">Loading…</p>}>
                  <LegalPage docKey={page} />
                </Suspense>
              </LegalPageBoundary>
            </div>
          </div>
        )}
        {page === "woocommerce" && <div className="legalWrap"><div className="container"><WooCommerceDocsPage /></div></div>}
        {page === "savings" && <SavingsPage onTalkToSales={goContactWithSummary} />}

        {page === "home" && (
          <>
            {/* ═══ HERO ═══ */}
            <section className="hero">
              <div className="heroGlow" aria-hidden="true" />
              <div className="container heroGrid">
                <div className="heroCopy reveal">
                  <div className="pill">
                    <span className="pillDot" />
                    Open Banking infrastructure
                  </div>

                  <h1 className="h1">
                    Bank payments that{" "}
                    <span className="gradientText">convert like cards</span>.
                  </h1>

                  <p className="heroLead">
                    Paybank2bank helps merchants unlock faster, lower-cost A2A
                    payments and account verification — with data-driven
                    intelligence built&nbsp;in.
                  </p>

                  <div className="heroCtas">
                    <button className="btn btnPrimary btnLg" onClick={() => openExternal(DASHBOARD_URL)}>
                      Dashboard
                      <span className="btnArrow">&rarr;</span>
                    </button>
                    <button className="btn btnSecondary btnLg" onClick={() => goHome("how-it-works")}>
                      See how it works
                    </button>
                  </div>

                  <div className="heroStats">
                    <div className="heroStat">
                      <div className="heroStatValue">99.99%</div>
                      <div className="heroStatLabel">Uptime</div>
                    </div>
                    <div className="heroStatDivider" />
                    <div className="heroStat">
                      <div className="heroStatValue">&lt;20s</div>
                      <div className="heroStatLabel">Settlement</div>
                    </div>
                    <div className="heroStatDivider" />
                    <div className="heroStat">
                      <div className="heroStatValue">21</div>
                      <div className="heroStatLabel">Markets</div>
                    </div>
                  </div>
                </div>

                <div className="heroVisual reveal" aria-hidden="true">
                  <div className="mockupCard">
                    <div className="mockupHeader">
                      <span className="mockupBadge">Live checkout</span>
                      <span className="mockupAmount">&pound;128.40</span>
                    </div>

                    <div className="mockupBody">
                      <div className="mockupRow">
                        <span className="mockupLabel">Merchant</span>
                        <span className="mockupValue">Acme Electronics</span>
                      </div>
                      <div className="mockupRow">
                        <span className="mockupLabel">Method</span>
                        <span className="mockupValue">Pay by bank</span>
                      </div>
                      <div className="mockupDivider" />

                      <div className="mockupBankLabel">Select your bank</div>
                      <div className="mockupBanks">
                        {banks.map((b) => (
                          <div key={b.name} className="mockupBank">
                            <span className="mockupBankLogo"><b.Logo /></span>
                            <span className="mockupBankName">{b.name}</span>
                          </div>
                        ))}
                      </div>
                    </div>

                    <div className="mockupFooter">
                      <div className="mockupRisk">
                        <span className="mockupRiskDot" />
                        Verified payer
                      </div>
                      <button className="mockupBtn">Continue &rarr;</button>
                    </div>
                  </div>

                  <div className="mockupSuccess">
                    <div className="mockupSuccessIcon">&#10003;</div>
                    <div className="mockupSuccessTitle">Payment sent</div>
                    <div className="mockupSuccessAmount">&pound;128.40</div>
                    <div className="mockupSuccessRef">Ref: INV-10482</div>
                    <div className="mockupSuccessTime">Settled in seconds</div>
                  </div>
                </div>
              </div>
            </section>

            {/* ═══ WHERE WE OPERATE (Carousel band — promoted to top) ═══ */}
            <section className="coverageBand" aria-label="Where we operate">
              <div className="container reveal">
                <div className="coverageBandHeader">
                  <div className="coverageBandLabel">
                    <span className="coverageBandIcon"><Icon.Globe /></span>
                    <span className="coverageBandTitle">Where we operate</span>
                  </div>
                  <span className="coverageBandMeta">Live in 21 markets</span>
                </div>
                <CountryCarousel countries={COUNTRIES} />
              </div>
            </section>

            {/* ═══ HOW IT WORKS ═══ */}
            <section id="how-it-works" className="section">
              <div className="container">
                <div className="sectionHeader reveal">
                  <div className="sectionLabel">How it works</div>
                  <h2 className="h2">Three steps. Zero friction.</h2>
                  <p className="sectionSub">
                    A payment experience designed to feel as smooth as tapping a card.
                  </p>
                </div>

                <div className="stepsGrid reveal">
                  <div className="step">
                    <div className="stepIcon"><Icon.Select /></div>
                    <div className="stepNumber">01</div>
                    <h3 className="stepTitle">Select</h3>
                    <p className="stepDesc">
                      Customer chooses their bank from an optimised selector with
                      smart defaults, search, and mobile-native flows.
                    </p>
                  </div>
                  <div className="step">
                    <div className="stepIcon"><Icon.Authorise /></div>
                    <div className="stepNumber">02</div>
                    <h3 className="stepTitle">Authorise</h3>
                    <p className="stepDesc">
                      Redirected to their banking app to confirm. QR codes for
                      desktop, instant redirects for mobile.
                    </p>
                  </div>
                  <div className="step">
                    <div className="stepIcon"><Icon.Confirmed /></div>
                    <div className="stepNumber">03</div>
                    <h3 className="stepTitle">Confirmed</h3>
                    <p className="stepDesc">
                      Payment complete. Real-time status via webhooks with rich
                      data signals and audit-ready event timelines.
                    </p>
                  </div>
                </div>
              </div>
            </section>

            {/* ═══ SOCIAL PROOF — Built for ═══ */}
            <section className="socialStrip">
              <div className="container reveal">
                <div className="socialBar">
                  <span className="socialLabel">Built for</span>
                  <div className="socialPills">
                    {["E-commerce", "Fintech", "Marketplaces", "Utilities", "Crypto", "Travel"].map(
                      (t) => (
                        <span key={t} className="socialPill">{t}</span>
                      )
                    )}
                  </div>
                </div>
              </div>
            </section>

            {/* ═══ SAVINGS CALCULATOR (teaser) ═══ */}
            <section id="savings" className="section sectionDark">
              <div className="container">
                <div className="sectionHeader reveal">
                  <div className="sectionLabelLight">Savings calculator</div>
                  <h2 className="h2Light">Stop overpaying for card processing</h2>
                  <p className="sectionSubLight">
                    Card scheme fees have risen over 25% in real terms since 2017.
                    See what pay-by-bank could save you — based on published
                    provider pricing and independent regulator data.
                  </p>
                </div>
                <div className="reveal">
                  <SavingsTeaser
                    onOpenCalculator={() => goPage("savings")}
                    onTalkToSales={goContactWithSummary}
                  />
                </div>
              </div>
            </section>

            {/* ═══ PRODUCTS ═══ */}
            <section id="products" className="section sectionSoft">
              <div className="container">
                <div className="sectionHeader reveal">
                  <div className="sectionLabel">Products</div>
                  <h2 className="h2">Three products. One platform.</h2>
                  <p className="sectionSub">
                    Move money, get paid, and verify identity — through a single
                    integration designed to grow with you.
                  </p>
                </div>

                <div className="productGrid reveal">
                  <div className="productCard">
                    <div className="productIcon"><Icon.Card /></div>
                    <div className="productKicker">Payments</div>
                    <h3 className="productTitle">Pay by bank, built for checkout</h3>
                    <p className="productDesc">
                      A2A payment initiation with a premium consumer experience —
                      bank selection, mobile redirects, and QR handoff optimised
                      for completion.
                    </p>
                    <ul className="productList">
                      <li>Hosted checkout and embedded API</li>
                      <li>Real-time status with signed webhooks</li>
                      <li>Multi-rail, multi-provider ready</li>
                      <li>Conversion-tuned bank discovery</li>
                    </ul>
                    <button className="btn btnPrimary btnFull" onClick={() => goHome("contact")}>
                      Demo payments
                    </button>
                  </div>

                  <div className="productCard">
                    <div className="productIcon"><Icon.Link /></div>
                    <div className="productKicker">Payment Links</div>
                    <h3 className="productTitle">Reusable links that collect until paid</h3>
                    <p className="productDesc">
                      Share a link, get paid. Each payment link stays active
                      until completion — reusable across channels and optimised
                      for every device.
                    </p>
                    <ul className="productList">
                      <li>Reusable until payment completion</li>
                      <li>QR code handoff for in-person and desktop</li>
                      <li>Mobile-optimised redirects and deep links</li>
                      <li>Responsive layouts and branded experience</li>
                    </ul>
                    <button className="btn btnPrimary btnFull" onClick={() => goHome("contact")}>
                      Demo payment links
                    </button>
                  </div>

                  <div className="productCard">
                    <div className="productIcon"><Icon.Verify /></div>
                    <div className="productKicker">Account Validation</div>
                    <h3 className="productTitle">Verify accounts with confidence</h3>
                    <p className="productDesc">
                      Confirm ownership, check account attributes, and enrich
                      onboarding with bank-grade signals — without degrading
                      the user experience.
                    </p>
                    <ul className="productList">
                      <li>Account ownership and details checks</li>
                      <li>Balance and metadata insights</li>
                      <li>Confidence signals for decisioning</li>
                      <li>Workflow-friendly callbacks</li>
                    </ul>
                    <button className="btn btnPrimary btnFull" onClick={() => goHome("contact")}>
                      Demo validation
                    </button>
                  </div>
                </div>

                <div className="featuresGrid reveal">
                  {[
                    { I: Icon.Conversion, title: "Conversion-first UX",
                      desc: "Checkout patterns designed to reduce drop-off: fast bank discovery, suggested banks, and mobile-native routes." },
                    { I: Icon.Brand, title: "Brandable experiences",
                      desc: "White-label the flow to match your product. Consistent styling and trust cues that feel premium." },
                    { I: Icon.Ops, title: "Operational clarity",
                      desc: "Statuses, audit trails, event timestamps, webhooks and analytics-friendly exports." },
                    { I: Icon.Scale, title: "Built for scale",
                      desc: "Serverless architecture patterns, resilient callbacks, and provider-aware routing." },
                  ].map((f) => (
                    <div key={f.title} className="featureItem">
                      <div className="featureIcon"><f.I /></div>
                      <div className="featureTitle">{f.title}</div>
                      <div className="featureDesc">{f.desc}</div>
                    </div>
                  ))}
                </div>
              </div>
            </section>

            {/* ═══ INTELLIGENCE (formerly Risk) ═══ */}
            <section id="intelligence" className="section sectionDark">
              <div className="container">
                <div className="splitGrid">
                  <div className="reveal">
                    <div className="sectionHeader">
                      <div className="sectionLabelLight">Intelligence</div>
                      <h2 className="h2Light">Smarter payments, powered by data</h2>
                      <p className="sectionSubLight">
                        Every payment journey generates rich, structured data.
                        We turn that data into actionable intelligence that lifts
                        approval rates, improves onboarding, and helps you say
                        "yes" to more good customers.
                      </p>
                    </div>

                    <div className="riskPoints">
                      {[
                        { title: "Conversion insights",
                          desc: "Spot patterns that correlate with successful bank handoffs and apply them across your funnel." },
                        { title: "Account intelligence",
                          desc: "Use ownership and metadata checks to reduce payment errors, returns, and disputes." },
                        { title: "Smart decisioning",
                          desc: "Route, retry, or step-up payments based on the confidence signals you choose to act on." },
                      ].map((r) => (
                        <div key={r.title} className="riskPoint">
                          <div className="riskTick">&#10003;</div>
                          <div>
                            <div className="riskTitle">{r.title}</div>
                            <div className="riskDesc">{r.desc}</div>
                          </div>
                        </div>
                      ))}
                    </div>

                    <div className="sectionCtas">
                      <button className="btn btnPrimary" onClick={() => goHome("contact")}>
                        Talk to our team
                      </button>
                      <button className="btn btnGhostLight" onClick={() => openExternal(CONNECT_URL)}>
                        Developer details
                      </button>
                    </div>
                  </div>

                  <div className="reveal">
                    <div className="analyticsCard">
                      <div className="analyticsHeader">
                        <span className="analyticsTitle">Conversion &amp; confidence</span>
                        <span className="analyticsMeta">Last 24h</span>
                      </div>

                      <div className="analyticsRows">
                        {[
                          { k: "Initiations", v: "3,482", trend: "+7.4%" },
                          { k: "Completions", v: "2,941", trend: "+5.1%" },
                          { k: "Drop-off", v: "15.5%", trend: "-2.0%" },
                          { k: "High-confidence", v: "91.2%", trend: "+0.8%" },
                        ].map((row) => (
                          <div key={row.k} className="analyticsRow">
                            <span className="analyticsK">{row.k}</span>
                            <span className="analyticsV">{row.v}</span>
                            <span
                              className={`analyticsTrend ${
                                row.trend.startsWith("-") ? "down" : "up"
                              }`}
                            >
                              {row.trend}
                            </span>
                          </div>
                        ))}
                      </div>

                      <div className="analyticsBars" aria-hidden="true">
                        {Array.from({ length: 20 }, (_, i) => (
                          <div
                            key={i}
                            className="analyticsBar"
                            style={{ height: `${20 + ((i * 17) % 70)}%` }}
                          />
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </section>

            {/* ═══ COVERAGE ═══ */}
            <section id="coverage" className="section">
              <div className="container">
                <div className="sectionHeader reveal">
                  <div className="sectionLabel">Coverage</div>
                  <h2 className="h2">One platform. Twenty-one markets. Thousands of banks.</h2>
                  <p className="sectionSub">
                    From the High Street to the Baltics, Paybank2bank meets your
                    customers where they bank — with native checkout flows,
                    localised journeys, and bank-grade reliability across the UK
                    and Europe.
                  </p>
                </div>

                <div className="coverageStats reveal">
                  <div className="coverageStat">
                    <div className="coverageStatValue">21</div>
                    <div className="coverageStatLabel">UK &amp; European markets</div>
                  </div>
                  <div className="coverageStatDivider" />
                  <div className="coverageStat">
                    <div className="coverageStatValue">1,800+</div>
                    <div className="coverageStatLabel">Connected banks</div>
                  </div>
                  <div className="coverageStatDivider" />
                  <div className="coverageStat">
                    <div className="coverageStatValue">450M+</div>
                    <div className="coverageStatLabel">Reachable consumers</div>
                  </div>
                  <div className="coverageStatDivider" />
                  <div className="coverageStat">
                    <div className="coverageStatValue">24 / 7</div>
                    <div className="coverageStatLabel">Real-time settlement</div>
                  </div>
                </div>

                <div className="coverageGrid reveal">
                  <div className="coverageCard">
                    <div className="coverageCardIcon"><Icon.Bolt /></div>
                    <h3 className="coverageTitle">Optimised checkout patterns</h3>
                    <p className="coverageDesc">
                      Mobile redirects, QR codes, smart defaults and search —
                      tuned per market for higher completion rates.
                    </p>
                  </div>

                  <div className="coverageCard">
                    <div className="coverageCardIcon"><Icon.Shield /></div>
                    <h3 className="coverageTitle">Bank-grade reliability</h3>
                    <p className="coverageDesc">
                      Resilient connectivity, smart retries, and routing logic
                      built to keep payments flowing through every market.
                    </p>
                  </div>

                  <div className="coverageCard">
                    <div className="coverageCardIcon"><Icon.Sparkle /></div>
                    <h3 className="coverageTitle">Localised experience</h3>
                    <p className="coverageDesc">
                      Local language, local logos, and the bank discovery
                      patterns customers in each region already trust.
                    </p>
                  </div>
                </div>
              </div>
            </section>

            {/* ═══ DEVELOPERS ═══ */}
            <section id="developers" className="section sectionDark">
              <div className="container">
                <div className="splitGrid">
                  <div className="reveal">
                    <div className="sectionHeader">
                      <div className="sectionLabelLight">Developers</div>
                      <h2 className="h2Light">Developer-friendly by default</h2>
                      <p className="sectionSubLight">
                        Clean integration patterns, robust callbacks, and an
                        experience you can fully white-label.
                      </p>
                    </div>

                    <div className="devPoints">
                      {[
                        { t: "Hosted + API", d: "Start with payment links, then move to embedded." },
                        { t: "Event timelines", d: "Track every state change for support and analytics." },
                        { t: "Secure callbacks", d: "Signed webhooks and audit-ready payloads." },
                        { t: "Serverless scale", d: "Built to handle spikes without re-platforming." },
                      ].map((x) => (
                        <div key={x.t} className="devPoint">
                          <div className="devDot" />
                          <div>
                            <div className="devTitle">{x.t}</div>
                            <div className="devDesc">{x.d}</div>
                          </div>
                        </div>
                      ))}
                    </div>

                    <div className="sectionCtas">
                      <button className="btn btnPrimary" onClick={() => openExternal(CONNECT_URL)}>
                        Get integration guidance
                      </button>
                      <button className="btn btnGhostLight" onClick={() => goPage("woocommerce")}>
                        WooCommerce plugin guide
                      </button>
                      <a className="btn btnGhostLight" href={WOO_APPLY_URL} target="_blank" rel="noopener noreferrer">
                        Apply for the WooCommerce plan
                      </a>
                    </div>
                    <p className="devPlanNote">
                      <strong>WooCommerce plan:</strong> {WOO_PLAN_PRICING}. {WOO_PLAN_VAT_NOTE}
                    </p>
                  </div>

                  <div className="reveal">
                    <div className="codeCard">
                      <div className="codeHeader">
                        <span className="codeTitle">Example flow</span>
                        <span className="codeMeta">Create &rarr; Redirect &rarr; Webhook</span>
                      </div>
                      <pre className="codeBlock">
{`POST /payment/create
{
  "amount": 128.40,
  "currency": "GBP",
  "reference": "INV-10482",
  "consumer": { "name": "Alex" }
}

→ { "pb2bId": "...", "redirectUrl": "..." }

Webhook: payment.status.changed
{
  "pb2bId": "...",
  "status": "initiated | accepted | failed",
  "bank": { "id": "...", "name": "..." },
  "timestamps": { ... }
}`}
                      </pre>
                    </div>
                  </div>
                </div>
              </div>
            </section>

            {/* ═══ CONTACT ═══ */}
            <section id="contact" className="section sectionSoft">
              <div className="container">
                <div className="contactGrid">
                  <div className="reveal">
                    <div className="sectionHeader">
                      <div className="sectionLabel">Get started</div>
                      <h2 className="h2">Let's build your pay-by-bank journey</h2>
                      <p className="sectionSub">
                        Tell us what you're launching — payments, verification,
                        or both. We'll recommend the fastest path to production.
                      </p>
                    </div>

                    <div className="contactPanel">
                      <div className="contactStats">
                        <div className="contactStat">
                          <div className="contactStatValue">21</div>
                          <div className="contactStatLabel">Markets</div>
                        </div>
                        <div className="contactStat">
                          <div className="contactStatValue">99.99%</div>
                          <div className="contactStatLabel">Uptime</div>
                        </div>
                        <div className="contactStat">
                          <div className="contactStatValue">&lt;20s</div>
                          <div className="contactStatLabel">Settlement</div>
                        </div>
                      </div>

                      <div className="contactDivider" />

                      <div className="contactRows">
                        <div className="contactRow">
                          <span className="contactRowIcon"><Icon.Bolt /></span>
                          <div>
                            <div className="contactRowTitle">Go live in days</div>
                            <div className="contactRowDesc">Hosted checkout or embedded API — your choice.</div>
                          </div>
                        </div>
                        <div className="contactRow">
                          <span className="contactRowIcon"><Icon.Globe /></span>
                          <div>
                            <div className="contactRowTitle">21 markets, one integration</div>
                            <div className="contactRowDesc">UK and Europe coverage from day one.</div>
                          </div>
                        </div>
                        <div className="contactRow">
                          <span className="contactRowIcon"><Icon.Shield /></span>
                          <div>
                            <div className="contactRowTitle">Bank-grade reliability</div>
                            <div className="contactRowDesc">Signed webhooks, audit-ready events, 99.99% uptime.</div>
                          </div>
                        </div>
                        <div className="contactRow">
                          <span className="contactRowIcon"><Icon.Conversion /></span>
                          <div>
                            <div className="contactRowTitle">Conversion intelligence</div>
                            <div className="contactRowDesc">Data-driven signals from every payment journey.</div>
                          </div>
                        </div>
                      </div>

                      <div className="contactDivider" />

                      <div className="contactActions">
                        <a className="contactCta" href={`mailto:${SUPPORT_EMAIL}`}>
                          <span className="contactCtaLabel">Prefer email?</span>
                          <span className="contactCtaValue">{SUPPORT_EMAIL}</span>
                        </a>
                        <button className="btn btnPrimary" onClick={() => openExternal(DASHBOARD_URL)}>
                          Open dashboard
                          <span className="btnArrow">&rarr;</span>
                        </button>
                      </div>

                      <div className="contactLogosBlock">
                        <div className="contactLogosLabel">Compatible with all major banks</div>
                        <div className="contactLogos" aria-hidden="true">
                          <span className="contactLogo"><BankLogo.Barclays /></span>
                          <span className="contactLogo"><BankLogo.HSBC /></span>
                          <span className="contactLogo"><BankLogo.NatWest /></span>
                          <span className="contactLogo"><BankLogo.Monzo /></span>
                          <span className="contactLogo"><BankLogo.Revolut /></span>
                          <span className="contactLogo"><BankLogo.Starling /></span>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="reveal">
                    <div className="sectionHeader">
                      <div className="sectionLabel">About you</div>
                      <h2 className="h2">Tell us about your project</h2>
                      <p className="sectionSub">
                        Share a few details and we'll line up a tailored demo —
                        usually within one business day.
                      </p>
                    </div>

                    <div className="formCard">
                      {formSent ? (
                        <div className="formSuccess">
                          <div className="formSuccessIcon">&#10003;</div>
                          <div className="formSuccessTitle">Thanks for reaching out</div>
                          <div className="formSuccessDesc">
                            A member of our team will be in touch shortly.
                          </div>
                        </div>
                      ) : (
                        <form className="form" onSubmit={handleFormSubmit} data-hs-do-not-collect="true">
                          <div className="formSection">
                            <div className="formSectionLabel">Your details</div>
                            <div className="formRow">
                              <label className="field">
                                <span className="fieldLabel">First name <span className="fieldReq">*</span></span>
                                <input className="input" name="firstname" placeholder="First name" required />
                              </label>
                              <label className="field">
                                <span className="fieldLabel">Last name <span className="fieldReq">*</span></span>
                                <input className="input" name="lastname" placeholder="Last name" required />
                              </label>
                            </div>
                            <label className="field">
                              <span className="fieldLabel">Work email <span className="fieldReq">*</span></span>
                              <input className="input" name="email" type="email" placeholder="name@company.com" required />
                            </label>
                            <div className="formRow">
                              <label className="field">
                                <span className="fieldLabel">Company</span>
                                <input className="input" name="company" placeholder="Company name" />
                              </label>
                              <label className="field">
                                <span className="fieldLabel">Industry</span>
                                <select className="input select" name="industry" defaultValue="">
                                  <option value="" disabled>Select industry</option>
                                  <option value="E-commerce">E-commerce</option>
                                  <option value="Fintech">Fintech</option>
                                  <option value="Banking">Banking</option>
                                  <option value="Marketplaces">Marketplaces</option>
                                  <option value="Utilities">Utilities</option>
                                  <option value="Crypto / Digital Assets">Crypto / Digital Assets</option>
                                  <option value="Travel">Travel</option>
                                  <option value="Insurance">Insurance</option>
                                  <option value="Gaming">Gaming</option>
                                  <option value="Other">Other</option>
                                </select>
                              </label>
                            </div>
                          </div>

                          <div className="formDivider" />

                          <div className="formSection">
                            <div className="formSectionLabel">What are you interested in?</div>
                            <div className="field">
                              <span className="fieldLabel">Products</span>
                              <div className="checkRow">
                                <label className="check">
                                  <input type="checkbox" name="products" value="PIS (Payments)" />
                                  <span className="checkBox" />
                                  <span className="checkLabel">PIS <span className="checkMeta">Payments</span></span>
                                </label>
                                <label className="check">
                                  <input type="checkbox" name="products" value="AIS (Account Information)" />
                                  <span className="checkBox" />
                                  <span className="checkLabel">AIS <span className="checkMeta">Account Info</span></span>
                                </label>
                              </div>
                            </div>
                            <div className="field">
                              <span className="fieldLabel">Region interest</span>
                              <div className="checkRow checkRowWrap">
                                {["UK", "Ireland", "DACH", "France", "Benelux", "Nordics", "Southern Europe", "Eastern Europe"].map((r) => (
                                  <label key={r} className="check">
                                    <input type="checkbox" name="regions" value={r} />
                                    <span className="checkBox" />
                                    <span className="checkLabel">{r}</span>
                                  </label>
                                ))}
                              </div>
                            </div>
                            <label className="field">
                              <span className="fieldLabel">Use case</span>
                              <select className="input select" name="usecase" defaultValue="">
                                <option value="" disabled>Select use case</option>
                                <option value="Checkout / E-commerce">Checkout / E-commerce</option>
                                <option value="Payment links">Payment links</option>
                                <option value="Account validation / KYC">Account validation / KYC</option>
                                <option value="Conversion intelligence">Conversion intelligence</option>
                                <option value="Other">Other</option>
                              </select>
                            </label>
                          </div>

                          <div className="formDivider" />

                          <div className="formSection">
                            <div className="formSectionLabel">Tell us more</div>
                            <label className="field">
                              <span className="fieldLabel">Message</span>
                              <textarea
                                key={contactPrefill || "blank"}
                                className="input textarea"
                                name="message"
                                placeholder="Tell us about your project, volumes, timeline..."
                                rows={contactPrefill ? 8 : 4}
                                defaultValue={contactPrefill}
                              />
                            </label>
                          </div>

                          <button
                            type="submit"
                            className="btn btnAccent btnGlow btnFull btnLg"
                            disabled={formSending}
                          >
                            {formSending ? "Sending..." : "Request a demo"}
                            {!formSending && <span className="btnArrow">&rarr;</span>}
                          </button>

                          {formError && (
                            <p className="formError" role="alert">
                              Sorry, your message didn't reach us. Please try again, or email{" "}
                              <a href={`mailto:${SUPPORT_EMAIL}`}>{SUPPORT_EMAIL}</a>.
                            </p>
                          )}

                          <p className="formPrivacy">
                            We'll use these details to reply to and follow up on your enquiry,
                            and to arrange a demo if you ask for one. We don't send newsletters
                            or marketing campaigns. See our{" "}
                            <SiteLink to="privacy" hash="website">Privacy policy</SiteLink>.
                          </p>
                        </form>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </section>
          </>
        )}
      </main>

      {/* ═══ FOOTER ═══ */}
      <footer className="footer">
        <div className="container footerInner">
          <div className="footerBrand">
            <img
              className="footerLogo"
              src="/pb2b-logo.png"
              alt="paybank2bank"
              onError={(e) => { e.currentTarget.style.display = "none"; }}
            />
            <div>
              <div className="footerName">paybank2bank</div>
              <div className="footerTag">
                Premium Open Banking payments and verification.
              </div>
              <div className="footerAddress">
                {COMPANY.legalName} · {COMPANY.address} · Trade Register no. {COMPANY.tradeRegister} ·
                CUI {COMPANY.cui}
                {COMPANY.shareCapital && ` · Share capital ${COMPANY.shareCapital} RON`}
              </div>
            </div>
          </div>

          <div className="footerCols">
            <div className="footerCol">
              <div className="footerColTitle">Company</div>
              <SiteLink className="footerLink" to="about">About us</SiteLink>
              <SiteLink className="footerLink" to="savings">Savings calculator</SiteLink>
              <button className="footerLink" onClick={() => goHome("coverage")}>Coverage</button>
              <button className="footerLink" onClick={() => goHome("contact")}>Contact</button>
              <a className="footerLink footerEmail" href={`mailto:${SUPPORT_EMAIL}`}>
                Email us
              </a>
            </div>
            <div className="footerCol">
              <div className="footerColTitle">Developers</div>
              <a className="footerLink" href={CONNECT_URL} target="_blank" rel="noopener noreferrer">
                Docs
              </a>
              <SiteLink className="footerLink" to="woocommerce">WooCommerce plugin</SiteLink>
              <a className="footerLink" href={DASHBOARD_URL} target="_blank" rel="noopener noreferrer">
                Dashboard
              </a>
            </div>
            <div className="footerCol">
              <div className="footerColTitle">Legal</div>
              <SiteLink className="footerLink" to="legal">Legal information</SiteLink>
              <SiteLink className="footerLink" to="privacy">Privacy policy</SiteLink>
              <SiteLink className="footerLink" to="cookies">Cookie policy</SiteLink>
              <SiteLink className="footerLink" to="consumer">Consumer terms</SiteLink>
              <SiteLink className="footerLink" to="client">Client terms</SiteLink>
              <SiteLink className="footerLink" to="website">Website terms</SiteLink>
              <button className="footerLink" onClick={() => setBannerOpen(true)}>Cookie settings</button>
            </div>
          </div>
        </div>

        <div className="container footerBottom">
          <p className="footerRegulatory">
            {REGULATORY_LINE}{" "}
            <SiteLink className="footerRegulatoryLink" to="legal" hash="regulatory">Regulatory information</SiteLink>
          </p>
          <span className="footerFine">
            &copy; {new Date().getFullYear()} {COMPANY.legalName}, trading as {COMPANY.tradingName}. All rights reserved.
          </span>
          <span className="footerFine">Built for conversion. Designed for trust.</span>
        </div>
      </footer>

      <CookieBanner open={bannerOpen} current={consent} onChoose={chooseCookies} />
    </div>
  );

  // Links rendered with <SiteLink> navigate through goPage
  return <NavContext.Provider value={goPage}>{content}</NavContext.Provider>;
}
